import type { FC, ReactNode } from 'react'
import { useRef, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'

export const Tooltip: FC<{ text: string; children: ReactNode; delay?: number }> = ({
  text,
  children,
  delay = 200,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        setPosition({
          top: rect.top - 8,
          left: rect.left + rect.width / 2,
        })
      }
      setIsVisible(true)
    }, delay)
  }

  const handleMouseLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setIsVisible(false)
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  return (
    <div
      role="group"
      ref={triggerRef}
      className="group relative inline-flex items-center justify-center border-0 bg-transparent p-0 cursor-default"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isVisible &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              transform: 'translate(-50%, -100%)',
              zIndex: 9999,
            }}
            className={`px-2.5 py-1 bg-popover text-popover-foreground text-[11px] font-medium rounded-sm border border-border shadow-lg whitespace-nowrap pointer-events-none`}
          >
            {text}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-popover" />
          </div>,
          document.body
        )}
    </div>
  )
}

// --- Utility Functions ---

export const isTextMime = (mime: string) => {
  return (
    mime.startsWith('text/') ||
    mime.includes('json') ||
    mime.includes('javascript') ||
    mime.includes('xml') ||
    mime.includes('csv') ||
    mime.includes('html')
  )
}

export const decodeBase64 = (b64: string) => {
  try {
    return atob(b64)
  } catch {
    return 'Unable to preview content'
  }
}

export const getDomain = (url: string) => {
  try {
    return new URL(url).hostname.replace('www.', '')
  } catch {
    return ''
  }
}

export const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return '0 Bytes'
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}
